<template>
  <div class="vpn-site">
    <HeadNav :selectedLanguage="localeKey" page="gb" />

    <main>
      <section class="hero">
        <div class="hero-media" :style="{ backgroundImage: `url(${heroImage})` }" aria-hidden="true" />
        <div class="hero-shade" aria-hidden="true" />
        <div class="container">
          <div class="hero-content">
            <h1>{{ content.h1 }}</h1>
            <p class="hero-lead">{{ content.heroLead }}</p>
            <ul class="hero-highlights">
              <li v-for="item in content.heroHighlights" :key="item.text">
                <span class="hero-highlight-icon" aria-hidden="true">{{ item.icon }}</span>
                <span>{{ item.text }}</span>
              </li>
            </ul>
            <div class="hero-actions">
              <router-link class="btn btn-primary" to="/download/">{{ content.getApk }}</router-link>
              <a class="btn btn-ghost" href="#features">{{ content.viewFeatures }}</a>
            </div>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="container prose">
          <h2>{{ content.whatTitle }}</h2>
          <p v-html="content.whatParagraph1" />
          <p v-html="content.whatParagraph2" />
          <p class="note" v-html="content.note" />
        </div>
      </section>

      <section class="section section-tint life-section">
        <div class="container">
          <div class="section-head">
            <h2>{{ content.lifeTitle }}</h2>
          </div>
          <div class="life-grid">
            <article v-for="item in content.lifeItems" :key="item.title" class="life-card">
              <div class="life-icon" aria-hidden="true">{{ item.icon }}</div>
              <div class="card-title">{{ item.title }}</div>
              <ul>
                <li v-for="entry in item.entries" :key="entry">{{ entry }}</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section id="features" class="section">
        <div class="container">
          <div class="section-head">
            <h2>{{ content.featuresTitle }}</h2>
            <p>{{ content.featuresLead }}</p>
          </div>
          <div class="feature-grid">
            <article v-for="feature in content.featureCards" :key="feature.title" class="feature-card">
              <div class="feature-media">
                <img :src="feature.image" :alt="feature.title">
              </div>
              <div class="card-title">{{ feature.title }}</div>
              <p>{{ feature.text }}</p>
            </article>
          </div>
          <div class="feature-prose">
            <article v-for="feature in content.featureTexts" :key="feature.title" class="feature-text">
              <div class="card-title">{{ feature.title }}</div>
              <p>{{ feature.text }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section section-tint install-section">
        <div class="container">
          <div class="section-head">
            <h2 v-html="content.installTitleHtml" />
            <p>{{ content.installLead }}</p>
          </div>
          <div class="install-grid">
            <article v-for="(step, index) in content.installSteps" :key="step.title" class="install-card">
              <span class="step-badge">{{ index + 1 }}</span>
              <div class="card-title">{{ step.title }}</div>
              <p v-html="step.text"></p>
            </article>
          </div>
          <div class="center-action">
            <router-link class="btn btn-primary" to="/download/">{{ content.getApk }}</router-link>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="container">
          <div class="section-head">
            <h2>{{ content.platformsTitle }}</h2>
            <p>{{ content.platformsLead }}</p>
          </div>
          <div class="platform-grid">
            <component
              :is="platform.to ? 'router-link' : 'div'"
              v-for="platform in content.platforms"
              :key="platform.name"
              class="platform-card"
              v-bind="platform.to ? { to: platform.to } : {}">
              <div class="platform-icon" v-html="platform.icon"></div>
              <span>{{ platform.name }}</span>
            </component>
          </div>
        </div>
      </section>

      <section class="section section-navy">
        <div class="container compare">
          <div class="section-head light">
            <h2>{{ content.compareTitle }}</h2>
            <p>{{ content.compareLead }}</p>
          </div>
          <div class="compare-table-wrap">
            <table class="compare-table">
              <thead>
                <tr>
                  <th v-for="header in content.compareHeaders" :key="header">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in content.compareRows" :key="row.feature">
                  <td>{{ row.feature }}</td>
                  <td>{{ row.vpn }}</td>
                  <td>{{ row.proxy }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h3 class="compare-choose-title">{{ content.compareChooseTitle }}</h3>
          <div class="compare-cards">
            <article class="compare-card preferred">
              <div class="card-title">
                <router-link class="home-link" to="/" v-html="content.chooseVpnTitle" />
              </div>
              <p>{{ content.chooseVpnText }}</p>
            </article>
            <article class="compare-card">
              <div class="card-title">
                <router-link class="home-link" to="/web-proxy/" v-html="content.chooseProxyTitle" />
              </div>
              <p>{{ content.chooseProxyText }}</p>
            </article>
          </div>
          <p class="compare-note">{{ content.compareNote }}</p>
        </div>
      </section>

      <section class="section">
        <div class="container">
          <div class="section-head">
            <h2>{{ content.reviewsTitle }}</h2>
            <p>{{ content.reviewsLead }}</p>
          </div>
          <div class="review-track">
            <article v-for="review in content.reviews" :key="review.name" class="review-card">
              <div class="review-top">
                <div class="avatar" :style="{ background: review.color }">{{ review.name.charAt(0) }}</div>
                <div>
                  <div class="review-name">{{ review.name }}</div>
                  <div class="stars" aria-label="5 star rating">
                    <span v-for="n in 5" :key="n">{{ starIcon }}</span>
                  </div>
                </div>
              </div>
              <p>{{ review.text }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section section-tint faq-section">
        <div class="container faq-section-inner">
          <div class="section-head">
            <h2>{{ content.faqTitle }}</h2>
          </div>
          <details v-for="(faq, index) in content.faqs" :key="faq.question" :open="index === 0">
            <summary>{{ faq.question }}</summary>
            <p>{{ faq.answer }}</p>
          </details>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/vpn-theme.scss';
import HeadNav from '@/components/HeadNav.vue';
import { getHomeContent } from '@/documents/vpnHomeContent';

const icon = {
  windows: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 5.5l8-1.2v7.2H3V5.5zm9-.9l9-1.3v9.4h-9V4.6zM3 13.2h8v7.3l-8-1.2v-6.1zm9 .2h9v9.3l-9-1.3V13.4z"/></svg>`,
  apple: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.7 12.6c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.6.8-3.3.8-.7 0-1.8-.8-3-.8-1.5 0-2.9.9-3.7 2.3-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.7 2.3 2.9 2.2 1.2-.1 1.6-.7 3-.7s1.8.7 3 .7c1.3 0 2.1-1.1 2.9-2.2.9-1.3 1.3-2.5 1.3-2.6-.03-.01-2.5-1-2.5-3.6zM14.8 5.8c.6-.8 1.1-1.9.9-3-.9 0-2 .6-2.6 1.4-.6.7-1.1 1.8-.9 2.9 1 .1 2-.5 2.6-1.3z"/></svg>`,
  linux: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.1 2.2c-.9 0-1.8.7-2.2 1.8-.3.8-.2 1.7.1 2.8l.2.7c-.8.8-1.4 1.9-1.6 3.1-.4 2 .2 3.8 1.5 5l-.4 1.5c-.4 1.4-.2 2.6.6 3.3.5.5 1.2.7 2 .7.7 0 1.4-.3 2-.8.6.5 1.3.8 2 .8.8 0 1.5-.2 2-.7.8-.7 1-1.9.6-3.3l-.4-1.5c1.3-1.2 1.9-3 1.5-5-.2-1.2-.8-2.3-1.6-3.1l.2-.7c.3-1.1.4-2 .1-2.8-.4-1.1-1.3-1.8-2.2-1.8-.6 0-1.2.3-1.7.8-.5-.5-1.1-.8-1.7-.8zm-1.7 3.1c.2 0 .5.1.8.4l.8.8.8-.8c.3-.3.6-.4.8-.4.2 0 .4.1.5.4.1.3.1.7-.1 1.3l-.2.7h-3.6l-.2-.7c-.2-.6-.2-1 0-1.3.1-.3.3-.4.4-.4zm.2 3.2h3l.1.4c.3 1.1.1 2.2-.6 3-.4.4-.9.7-1.5.8h-.1c-.6-.1-1.1-.4-1.5-.8-.7-.8-.9-1.9-.6-3l.2-.4zm-1.5 5.1c.4.3.9.5 1.4.6v1.8c0 .5-.1.9-.3 1.1-.1.1-.2.2-.4.2-.2 0-.3-.1-.4-.2-.3-.3-.4-.9-.2-1.6l.1-.4-.2-1.5zm5.4 0l-.2 1.5.1.4c.2.7.1 1.3-.2 1.6-.1.1-.2.2-.4.2-.2 0-.3-.1-.4-.2-.2-.2-.3-.6-.3-1.1v-1.8c.5-.1 1-.3 1.4-.6z"/></svg>`,
  android: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M7.2 8.8l-1.4-2.4.9-.5 1.4 2.5c.9-.4 1.9-.6 2.9-.6s2 .2 2.9.6l1.4-2.5.9.5-1.4 2.4C17.1 9.8 18 11.3 18 13v5.5c0 .8-.7 1.5-1.5 1.5H16v2c0 .6-.4 1-1 1s-1-.4-1-1v-2h-4v2c0 .6-.4 1-1 1s-1-.4-1-1v-2h-.5c-.8 0-1.5-.7-1.5-1.5V13c0-1.7.9-3.2 2.2-4.2zM9.5 12.2a1 1 0 100 2 1 1 0 000-2zm5 0a1 1 0 100 2 1 1 0 000-2z"/></svg>`,
  ios: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.7 12.6c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.6.8-3.3.8-.7 0-1.8-.8-3-.8-1.5 0-2.9.9-3.7 2.3-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.7 2.3 2.9 2.2 1.2-.1 1.6-.7 3-.7s1.8.7 3 .7c1.3 0 2.1-1.1 2.9-2.2.9-1.3 1.3-2.5 1.3-2.6-.03-.01-2.5-1-2.5-3.6z"/></svg>`,
  chrome: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 00-8.7 5h7.2A3.5 3.5 0 0115 9.3L18.7 3A10 10 0 0012 2zM3.1 8.5A10 10 0 0012 22a10 10 0 008.6-5H12a3.5 3.5 0 01-3-1.7L3.1 8.5zM12 15.5A3.5 3.5 0 1112 8.5a3.5 3.5 0 010 7zM13.8 12l4.7 8.1A10 10 0 0022 12c0-.7-.1-1.4-.2-2h-7.3c.2.6.3 1.3.3 2z"/></svg>`,
  firefox: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c1.4 0 3.3.7 4.5 1.6-.8.2-1.5.5-2.1.9 1.7.1 3.2 1 4.1 2.4-.6-.1-1.2-.1-1.8 0 1.4.8 2.4 2.2 2.7 3.8-1.1-1.5-2.8-2.4-4.7-2.4-2.7 0-4.1 1.7-4.5 2.4-.2-.8-.1-1.6.2-2.4C8.6 7 7.4 8.4 7 10c-.9-1.5-.8-3.5.2-5.2C5 6.4 3.8 9 3.8 11.8 3.8 17 8 21.2 13.2 21.2c4.4 0 8.1-3 9.1-7.1C23.5 8.7 19.4 2 12 2z"/></svg>`,
  edge: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C7.4 2 3.7 5.2 2.6 9.4c1.4-1.9 3.7-3.2 6.4-3.2 1.2 0 2.2.2 3.1.6-2.7.8-4.7 3.3-4.7 6.3 0 .5 0 1 .1 1.4A8.1 8.1 0 003.5 12C2.5 14.8 3.7 18.5 8 20.4c.8.4 1.7.6 2.6.7-.7-.5-1.2-1.3-1.2-2.2 0-1.5 1.3-2.5 3.4-2.5h7.6c.4-1 .6-2 .6-3.1C20.9 6.5 16.9 2 12 2zm1.4 17.8c-1.7 0-3.1.8-3.1 1.9 0 .4.2.8.5 1.1 1 .1 2 .1 3-.1 2.7-.5 5-2.1 6.3-4.3h-5.5c-.7 0-1.2-.2-1.2-.6z"/></svg>`,
  appletv: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.2 7.2c-.5 0-1.1.3-1.5.7-.4.4-.7 1-.6 1.6.6.1 1.2-.2 1.6-.6.4-.4.7-1 .5-1.7zM14.9 9.8c-.9 0-1.6.5-2.1.5s-1.2-.5-2-.5c-1 0-2 .6-2.5 1.5-1.1 1.9-.3 4.7.8 6.2.5.8 1.1 1.6 1.9 1.6.7 0 1-.5 2-.5s1.2.5 2 .5c.8 0 1.4-.8 1.9-1.6.6-.9.8-1.7.8-1.8-.1 0-1.6-.6-1.6-2.5 0-1.6 1.3-2.3 1.3-2.4-.7-1.1-1.9-1.2-2.5-1.5zM4 16.5V8.8h1.4v6.3h3.3v1.4H4z"/></svg>`,
  firetv: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 8.5h2.1c1.5 0 2.4.8 2.4 2.1 0 1.4-.9 2.2-2.4 2.2H5.4v2.7H4V8.5zm1.4 3.1h.6c.7 0 1.1-.3 1.1-1s-.4-1-1.1-1h-.6v2zM10 15.5V8.5h1.4v7H10zm3.2 0V8.5h1.3l2.2 4.5h.1V8.5H18v7h-1.3l-2.2-4.5h-.1v4.5h-1.2zm7.3-2.5l1.5.4c-.3 1.2-1.3 2.1-2.8 2.1-1.9 0-3.2-1.4-3.2-3.5s1.3-3.5 3.2-3.5c1.5 0 2.5.8 2.9 2l-1.5.4c-.2-.7-.7-1.1-1.4-1.1-.9 0-1.6.8-1.6 2.2s.7 2.2 1.6 2.2c.7 0 1.2-.4 1.3-1.2z"/></svg>`,
};

export default {
  name: 'Home',
  components: {
    HeadNav,
  },
  computed: {
    localeKey() {
      return this.$route.meta.localeKey || 'en';
    },
    content() {
      const localized = getHomeContent(this.localeKey);
      return localized ? { ...this.$data, ...localized } : this.$data;
    },
  },
  data() {
    return {
      heroImage: require('@/assets/vpn/hero-site.webp'),
      starIcon: '\u2605',
      h1: 'Secure VPN - Fast, Private & Unlimited Access',
      heroLead:
        'Download Secure VPN and enjoy a fast, encrypted connection for browsing, streaming, gaming, and online privacy.',
      whatTitle: 'What Is Secure VPN',
      whatParagraph1:
        '<a class="home-link" href="/">Secure VPN</a> is a simple VPN app that helps you stay private while using the internet. With just one tap, you can connect to a secure server, hide your <a class="home-link" href="/what-is-my-ip/">IP address</a>, and browse more safely on public Wi-Fi, mobile networks, or home internet. It\'s a popular choice for users who want a fast connection without complicated settings.',
      whatParagraph2:
        'Whether you\'re streaming videos, checking emails, or browsing your favorite websites, <strong>Secure VPN</strong> provides an easy way to add an extra layer of privacy to your online activity.',
      note:
        'Secure VPN is a trademark of its respective owner. We\'re an independent resource providing information, user guides, and version updates. <em>Secure VPN is not supported in mainland China</em>.',
      lifeTitle: 'How Secure VPN Fits Into Your Daily Online Life',
      featuresTitle: 'Secure VPN Features',
      featuresLead: 'Everything you need for private browsing, streaming, and everyday protection.',
      installTitleHtml: 'How to Download & <strong>Install</strong> Secure VPN App',
      installLead: 'Follow these simple steps to download and install Secure VPN:',
      getApk: 'Get APK',
      viewFeatures: 'View Features',
      platformsTitle: 'Secure VPN Works all Major Platforms',
      platformsLead:
        'From smartphones to desktop computers, Secure VPN is designed to work on the devices you use every day. Install the app, connect to a server, and enjoy a faster, more private browsing experience wherever you go.',
      compareTitle: 'Secure VPN App vs Free Web Proxy Online',
      compareLead:
        'A Secure VPN App and a free web proxy both provide ways to access the internet more privately, but they work differently. A VPN app offers complete device protection with encrypted connections, while a web proxy is mainly designed for quick browser-based access.',
      compareHeaders: ['Feature', 'Secure VPN App', 'Free Web Proxy Online'],
      compareChooseTitle: 'Which One Should You Choose?',
      chooseVpnTitle: 'Choose <strong>Secure VPN App</strong>',
      chooseVpnText: 'if you want stronger privacy, faster connections, and protection for your entire device.',
      chooseProxyTitle: 'Choose a <strong>Free Web Proxy Online</strong>',
      chooseProxyText: 'if you only need temporary browser access for simple browsing tasks.',
      compareNote:
        'For regular internet activities, Secure VPN App provides a more complete and reliable solution for staying connected securely.',
      reviewsTitle: 'Trusted by Users',
      reviewsLead:
        'Secure VPN is trusted by users around the world for fast, stable, and secure internet connections. With reliable servers and strong privacy protection, it helps users browse, stream, game, and stay connected safely every day.',
      faqTitle: 'FAQ',
      heroHighlights: [
        { icon: '\u26A1', text: 'Fast & Stable VPN Servers' },
        { icon: '\uD83D\uDD12', text: 'Secure Encrypted Connection' },
        { icon: '\uD83C\uDF0D', text: '40+ Server Locations' },
        { icon: '\uD83D\uDCF1', text: 'One-Tap Connect, No Registration' },
      ],
      lifeItems: [
        { icon: '\uD83C\uDFAE', title: 'Gaming Without Network Limits', entries: ['PUBG Mobile', 'Call of Duty: Mobile', 'Free Fire', 'Mobile Legends'] },
        { icon: '\uD83D\uDCAC', title: 'Private Video Calls & Messaging', entries: ['WhatsApp', 'Telegram', 'Skype', 'Viber'] },
        { icon: '\uD83D\uDCF6', title: 'Stay Connected on Public Wi-Fi', entries: ['Public Wi-Fi', 'Workplaces', 'Schools', 'Airports'] },
        { icon: '\uD83D\uDEE1\uFE0F', title: 'Protect Your Online Privacy', entries: ['Strong Encryption', 'Hide IP Address', 'No-Log Policy', 'ISP Privacy'] },
      ],
      featureCards: [
        { title: 'Encrypted Online Security', text: 'Protect your online activity with strong encryption to keep your personal data and internet traffic private.', image: require('@/assets/vpn/screenshot-8.webp') },
        { title: 'Fast Global Servers', text: 'Connect to high-speed VPN servers around the world for smooth browsing, streaming, and everyday internet use.', image: require('@/assets/vpn/screenshot-4.webp') },
        { title: 'Multiple Server Locations', text: 'Choose from servers in different countries to enjoy a stable connection wherever you are.', image: require('@/assets/vpn/screenshot-6.webp') },
        { title: 'One-Tap Connection', text: 'Connect to the best available server instantly with a single tap\u2014no complicated setup required.', image: require('@/assets/vpn/screenshot-3.webp') },
        { title: 'Servers Worldwide', text: 'Connect to global VPN servers for fast, secure, and reliable browsing wherever you are, anytime you need.', image: require('@/assets/vpn/screenshot-2.webp') },
        { title: 'Smooth Streaming', text: 'Enjoy fast and stable connections for videos, live streaming, and other online entertainment.', image: require('@/assets/vpn/screenshot-5.webp') },
        { title: 'High-Speed Performance', text: 'Optimized servers deliver fast, stable connections for browsing, gaming.', image: require('@/assets/vpn/screenshot-7.webp') },
        { title: '100% Free & Uncapped', text: 'Enjoy unlimited VPN access with no daily data limits or hidden charges.', image: require('@/assets/vpn/screenshot-1.webp') },
      ],
      featureTexts: [
        { title: 'Lightweight & Easy to Use', text: 'The clean interface and simple controls make Secure VPN easy for both beginners and experienced users.' },
        { title: 'Public Wi-Fi Protection', text: 'Stay safer when using public Wi-Fi at caf\u00E9s, airports, hotels, or other shared networks.' },
        { title: 'Hide Your IP Address', text: 'Mask your real IP address to improve online privacy and reduce unwanted tracking while browsing.' },
        { title: 'Browse Without Limits', text: 'Explore the web with fewer restrictions and enjoy a more open online experience wherever you go.' },
        { title: 'Fast & Stable Servers', text: "Whether you're browsing, streaming, or just checking email, stable servers help keep your connection running smoothly." },
        { title: 'No-Log Policy', text: "Your online activity is yours. That's why we don't keep records of the websites you visit while using the VPN." },
      ],
      compareRows: [
        { feature: 'Installation', vpn: 'Easy app installation', proxy: 'No installation required' },
        { feature: 'Usage', vpn: 'Protects all apps and websites', proxy: 'Works only in your browser' },
        { feature: 'Privacy Protection', vpn: 'Strong encryption and secure connection', proxy: 'Basic browsing privacy' },
        { feature: 'Connection Speed', vpn: 'Optimized VPN servers', proxy: 'Depends on proxy server' },
        { feature: 'Device Protection', vpn: 'Full device protection', proxy: 'Browser-level only' },
        { feature: 'Streaming & Gaming', vpn: 'Better performance and stability', proxy: 'Limited performance' },
        { feature: 'Public Wi-Fi Security', vpn: 'Helps secure public Wi-Fi connections', proxy: 'Limited protection' },
        { feature: 'Server Options', vpn: 'Multiple server locations', proxy: 'Usually limited options' },
      ],
      installSteps: [
        { title: 'Visit the Download Page', text: 'Go to the Secure VPN download page or a trusted app store to get the latest version of the app.' },
        { title: 'Download Secure VPN', text: 'Tap the <strong>Download</strong> button and wait for the APK or app file to finish downloading on your device.' },
        { title: 'Enable Installation Permission', text: 'If you download the APK file, open your device settings and enable <strong>Install Unknown Apps</strong> permission for your browser or file manager.' },
        { title: 'Install the App', text: 'Open the downloaded file and tap <strong>Install</strong>. Wait until the installation process is complete.' },
        { title: 'Open and Connect', text: 'Launch Secure VPN, select a server location, and tap <strong>Connect</strong> to start a secure VPN connection.' },
        { title: 'Enjoy Secure Browsing', text: 'Your internet traffic is now protected. You can browse, stream, game, and use public Wi-Fi with a more secure connection.' },
      ],
      platforms: [
        { name: 'Windows', icon: icon.windows, to: '/for-pc/' },
        { name: 'macOS', icon: icon.apple, to: '/for-mac/' },
        { name: 'Linux', icon: icon.linux, to: '/for-linux/' },
        { name: 'Android', icon: icon.android, to: '/for-android/' },
        { name: 'iOS', icon: icon.ios, to: '/for-ios/' },
        { name: 'Chrome', icon: icon.chrome },
        { name: 'Firefox', icon: icon.firefox },
        { name: 'Edge', icon: icon.edge },
        { name: 'Apple TV', icon: icon.appletv },
        { name: 'FireTV', icon: icon.firetv },
      ],
      reviews: [
        { name: 'Alex', color: 'linear-gradient(135deg, #5b4fe8, #3b69ff)', text: 'Fast connection and easy to use. I can browse safely wherever I go.' },
        { name: 'Sarah', color: 'linear-gradient(135deg, #8b7cff, #5b4fe8)', text: 'Secure VPN works smoothly and keeps my connection stable on public Wi-Fi.' },
        { name: 'Mike', color: 'linear-gradient(135deg, #2a2a6a, #5b4fe8)', text: 'Great speed and reliable servers for streaming, gaming, and daily use.' },
      ],
      faqs: [
        { question: '1. Does a VPN slow down my internet?', answer: 'A VPN may slightly reduce your connection speed because your traffic is routed through a secure server. In most cases, the difference is small and you can still browse, stream, and use apps normally.' },
        { question: '2. Is SecureVPN safe to use?', answer: 'SecureVPN helps protect your online privacy by encrypting your connection and hiding your IP address. Always download the app from trusted sources and check the privacy policy before use.' },
        { question: '3. Is it legal to use a VPN?', answer: 'In most countries, using a VPN is legal and commonly used for online privacy and security. Users should follow the laws and regulations that apply in their location.' },
        { question: '4. How long can I keep SecureVPN connected?', answer: 'You can keep SecureVPN connected while you need a protected internet connection. Connection limits may vary depending on the app version and service conditions.' },
        { question: '5. How to use Secure VPN?', answer: 'Install Secure VPN, open the app, choose a server if available, and tap the connect button. Once connected, your internet traffic will be routed through the VPN.' },
        { question: '6. Does using Secure VPN affect battery life?', answer: "Secure VPN may use a small amount of additional battery because it keeps your connection active. In most cases, the impact is minimal and shouldn't noticeably affect normal daily use." },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
.vpn-site {
  --home-section-y: 56px;
  --home-section-y-mobile: 38px;
  --home-head-gap: 22px;
  --home-block-gap: 18px;

  background:
    radial-gradient(ellipse 80% 50% at 10% -10%, rgba(91, 79, 232, 0.12), transparent 55%),
    radial-gradient(ellipse 60% 40% at 90% 0%, rgba(59, 105, 255, 0.1), transparent 50%),
    #f5f4fb;
  color: var(--vpn-ink);
  font-family: var(--vpn-font);
}

.container {
  width: var(--vpn-container);
  margin: 0 auto;
}

.hero {
  position: relative;
  min-height: 560px;
  display: flex;
  align-items: flex-start;
  overflow: hidden;
  color: #fff;
}

.hero-media {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: 72% center;
  background-repeat: no-repeat;
  transform: scale(1.02);
  animation: vpn-fade 1s ease both;
}

.hero-shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(4, 18, 32, 0.92) 0%, rgba(4, 18, 32, 0.78) 34%, rgba(4, 18, 32, 0.28) 58%, rgba(4, 18, 32, 0.06) 100%),
    linear-gradient(180deg, rgba(4, 18, 32, 0.18) 0%, transparent 28%, transparent 72%, rgba(4, 18, 32, 0.22) 100%);
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 1;
  width: min(640px, 100%);
  margin: 0 auto 0 0;
  padding: 48px 0 52px;
  animation: vpn-rise 0.7s ease both;
}

.hero h1 {
  margin: 0 0 28px;
  font-size: clamp(28px, 3.8vw, 44px);
  line-height: 1.18;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #fff;
  text-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
  white-space: normal;
}

.hero-lead {
  margin: 0 0 32px;
  max-width: none;
  width: 100%;
  font-size: 17px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.82);
}

.hero-highlights {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  list-style: none !important;
  margin: 0 0 36px !important;
  padding: 0;
  max-width: none;
  width: 100%;
}

.hero-highlights li {
  list-style: none !important;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0;
  background: none;
  border: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.94);
}

.hero-highlight-icon {
  flex-shrink: 0;
  width: auto;
  height: auto;
  font-size: 16px;
}

.hero-actions,
.center-action {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}

.center-action {
  justify-content: center;
  margin-top: var(--home-block-gap);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  padding: 0 26px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 15px;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.btn:hover {
  transform: translateY(-2px);
}

.btn-primary {
  background: linear-gradient(135deg, var(--vpn-teal-bright), var(--vpn-teal));
  color: #fff;
  box-shadow: 0 12px 28px rgba(91, 79, 232, 0.35);
}

.btn-ghost {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: #fff;
  backdrop-filter: blur(8px);
}

.section {
  padding: var(--home-section-y) 0;
}

.section-tint {
  background:
    linear-gradient(180deg, rgba(239, 237, 255, 0.75), rgba(245, 244, 251, 0.95));
}

.section-navy {
  background:
    radial-gradient(ellipse 70% 80% at 80% 20%, rgba(91, 79, 232, 0.28), transparent 50%),
    linear-gradient(145deg, #12122e, #1e1b4b 55%, #2a2a6a);
  color: #fff;
}

.section-head {
  width: 100%;
  max-width: 100%;
  margin-bottom: var(--home-head-gap);
  box-sizing: border-box;
}

.section-head h2 {
  width: 100%;
  max-width: 100%;
  margin: 0 0 14px;
}

.section-head.light h2,
.section-navy h2,
.section-navy h3 {
  color: #fff;
}

.section-head.light p,
.section-navy p {
  color: rgba(255, 255, 255, 0.78);
}

.section-head p {
  margin: 0;
  color: var(--vpn-ink-soft);
  font-size: 17px;
  line-height: 1.7;
}

h2 {
  margin: 0 0 14px;
  font-size: clamp(26px, 2.8vw, 34px);
  line-height: 1.25;
  letter-spacing: -0.02em;
  color: var(--vpn-ink);
  font-weight: 800;
}

h3 {
  color: var(--vpn-ink);
  line-height: 1.3;
  font-weight: 700;
}

p,
li {
  font-size: 16px;
  line-height: 1.75;
  color: var(--vpn-ink-soft);
}

.prose {
  max-width: none;
}

.container.prose {
  width: var(--vpn-container);
}

.prose p {
  margin: 0 0 16px;
}

.prose ::v-deep .home-link {
  color: inherit;
  font-weight: inherit;
  text-decoration: underline;
  text-decoration-color: rgba(91, 79, 232, 0.35);
  text-underline-offset: 2px;
}

.prose .home-link:hover {
  color: var(--vpn-teal-deep);
}

.note {
  margin-top: 24px !important;
  padding: 16px 18px;
  border-radius: var(--vpn-radius-sm);
  background: linear-gradient(90deg, rgba(91, 79, 232, 0.12), rgba(91, 79, 232, 0.04));
  border-left: 3px solid var(--vpn-teal);
  color: var(--vpn-ink);
}

.life-grid,
.feature-grid,
.install-grid {
  display: grid;
  gap: 16px;
}

.life-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  width: 100%;
}

.life-section .section-head,
.life-section .life-grid {
  width: 100%;
  max-width: 100%;
}

.life-section .section-head h2 {
  width: 100%;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: normal;
  word-break: normal;
}

.life-card,
.feature-card,
.install-card {
  background: var(--vpn-surface);
  border: 1px solid var(--vpn-line);
  border-radius: var(--vpn-radius);
  padding: 24px;
  box-shadow: var(--vpn-shadow-soft);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.feature-card {
  overflow: hidden;
  padding-top: 0;
}

.life-card:hover,
.feature-card:hover,
.install-card:hover,
.platform-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--vpn-shadow);
}

.life-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  margin: 0 auto 14px;
  background: var(--vpn-surface-tint);
  font-size: 22px;
}

.life-card .card-title {
  margin: 0 0 12px;
  font-size: 17px;
  color: var(--vpn-ink);
  line-height: 1.3;
  font-weight: 700;
  text-align: center;
}

.life-card ul {
  margin: 0;
  padding-left: 18px;
}

.life-card li {
  margin-bottom: 6px;
  font-size: 14px;
}

.feature-grid {
  grid-template-columns: repeat(4, 1fr);
}

.feature-media {
  margin: 0 -24px 16px;
  overflow: hidden;
}

.feature-card img {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 0;
  box-shadow: none;
}

.feature-card .card-title {
  margin: 0 0 8px;
  font-size: 17px;
  color: var(--vpn-ink);
  line-height: 1.3;
  font-weight: 700;
}

.feature-card p {
  margin: 0;
  font-size: 14px;
  line-height: 1.65;
}

.feature-prose {
  width: 100%;
  margin-top: 28px;
  padding: 22px 24px;
  border-radius: var(--vpn-radius);
  background: #fff;
  border: 1px solid var(--vpn-line);
  box-shadow: var(--vpn-shadow-soft);
}

.feature-text {
  margin: 0;
  padding: 16px 0;
  border-bottom: 1px solid rgba(15, 48, 74, 0.08);
  background: none;
}

.feature-text:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.feature-text .card-title {
  margin: 0 0 10px;
  font-size: 20px;
  font-weight: 700;
  color: var(--vpn-ink);
  line-height: 1.3;
}

.feature-text p {
  margin: 0;
  font-size: 14px;
  line-height: 1.75;
  color: var(--vpn-ink-soft);
}

.install-grid {
  grid-template-columns: repeat(3, 1fr);
}

.install-card {
  min-height: 220px;
  background:
    linear-gradient(180deg, #fff, #f7fcfa);
}

.step-badge {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  background: linear-gradient(135deg, var(--vpn-teal), var(--vpn-teal-deep));
  color: #fff;
  font-weight: 800;
  box-shadow: 0 8px 18px rgba(91, 79, 232, 0.28);
}

.install-card .card-title {
  margin: 0 0 10px;
  font-size: 18px;
  color: var(--vpn-ink);
  line-height: 1.3;
  font-weight: 700;
}

.install-card p {
  margin: 0;
  font-size: 14px;
}

.install-card p::v-deep strong,
.section-head h2 strong {
  font-weight: 800;
  color: var(--vpn-ink);
}

.platform-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
}

.platform-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 118px;
  padding: 18px 12px;
  border-radius: var(--vpn-radius);
  background: #fff;
  border: 1px solid var(--vpn-line);
  box-shadow: var(--vpn-shadow-soft);
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.platform-card:hover {
  border-color: rgba(91, 79, 232, 0.35);
}

.platform-icon {
  width: 42px;
  height: 42px;
  color: var(--vpn-navy-mid);
  display: grid;
  place-items: center;
}

.platform-icon::v-deep svg {
  width: 34px;
  height: 34px;
}

.platform-card span {
  font-size: 13px;
  font-weight: 700;
  color: var(--vpn-ink);
  text-align: center;
}

.compare-table-wrap {
  width: 100%;
  margin: 0 0 28px;
  overflow-x: auto;
  border-radius: var(--vpn-radius);
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
}

.compare-table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  background: transparent;
}

.compare-table th,
.compare-table td {
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding: 14px 18px;
  color: rgba(255, 255, 255, 0.88);
  font-size: 15px;
  line-height: 1.45;
  text-align: left;
  vertical-align: top;
}

.compare-table th {
  background: linear-gradient(135deg, rgba(91, 79, 232, 0.28), rgba(255, 255, 255, 0.06));
  color: #fff;
  font-weight: 800;
  letter-spacing: 0.01em;
  border-bottom: 1px solid rgba(139, 124, 255, 0.28);
}

.compare-table th:first-child,
.compare-table td:first-child {
  font-weight: 700;
  color: #fff;
  width: 22%;
}

.compare-table tbody tr:nth-child(even) td {
  background: rgba(255, 255, 255, 0.03);
}

.compare-table tbody tr:hover td {
  background: rgba(91, 79, 232, 0.1);
}

.compare-table tbody tr:last-child td {
  border-bottom: 0;
}

.compare-table td:nth-child(2) {
  color: var(--vpn-teal-bright);
  font-weight: 600;
}

.compare-choose-title {
  margin: 0 0 16px;
  font-size: 26px;
  font-weight: 800;
  color: #fff;
}

.compare-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 18px;
}

.compare-card {
  padding: 24px;
  border-radius: var(--vpn-radius);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.compare-card.preferred {
  background: linear-gradient(145deg, rgba(91, 79, 232, 0.22), rgba(255, 255, 255, 0.06));
  border-color: rgba(139, 124, 255, 0.35);
}

.compare-card .card-title {
  margin: 0 0 10px;
  font-size: 20px;
  color: #fff;
  line-height: 1.3;
  font-weight: 700;
}

.compare-card .card-title strong {
  font-weight: 800;
  color: var(--vpn-teal-bright);
}

.compare-card .home-link {
  color: inherit;
  text-decoration: none;
}

.compare-card .home-link:hover strong {
  color: #fff;
}

.compare-card p,
.compare-note {
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
}

.review-track {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.review-card {
  padding: 24px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid var(--vpn-line);
  box-shadow: var(--vpn-shadow-soft);
}

.review-top {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: 800;
  font-size: 18px;
  flex-shrink: 0;
}

.review-name {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: var(--vpn-ink);
  line-height: 1.3;
}

.stars {
  color: var(--vpn-gold);
  font-size: 13px;
  letter-spacing: 1px;
  line-height: 1;
}

.review-card p {
  margin: 0;
  font-size: 14px;
  color: var(--vpn-muted);
}

.faq-section-inner details {
  max-width: none;
}

details {
  background: #fff;
  border: 1px solid var(--vpn-line);
  border-radius: var(--vpn-radius-sm);
  margin-bottom: 8px;
  padding: 0 20px;
  box-shadow: var(--vpn-shadow-soft);
}

summary {
  cursor: pointer;
  font-weight: 700;
  padding: 14px 0;
  color: var(--vpn-ink);
  list-style: none;
}

summary::-webkit-details-marker {
  display: none;
}

summary::after {
  content: '+';
  float: right;
  color: var(--vpn-teal);
  font-size: 20px;
  font-weight: 500;
}

details[open] summary::after {
  content: '-';
}

details p {
  margin: 0 0 16px;
  padding-top: 0;
}

@media (max-width: 1020px) {
  .life-grid,
  .feature-grid,
  .platform-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .install-grid,
  .compare-cards,
  .review-track {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 760px) {
  .section {
    padding: var(--home-section-y-mobile) 0;
  }

  .section-head {
    margin-bottom: 22px;
  }

  .hero {
    min-height: 520px;
    align-items: flex-end;
  }

  .hero-media {
    background-position: 58% center;
  }

  .hero-shade {
    background:
      linear-gradient(180deg, rgba(4, 18, 32, 0.35) 0%, rgba(4, 18, 32, 0.55) 28%, rgba(4, 18, 32, 0.92) 72%, rgba(4, 18, 32, 0.96) 100%);
  }

  .hero-content {
    width: 100%;
    padding: 36px 0 40px;
  }

  .life-grid,
  .feature-grid,
  .install-grid,
  .platform-grid,
  .compare-cards {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .feature-prose {
    margin-top: 24px;
    padding: 18px 16px;
  }

  .review-track {
    display: flex;
    gap: 14px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    padding-bottom: 8px;
    -webkit-overflow-scrolling: touch;
  }

  .review-card {
    flex: 0 0 min(84vw, 320px);
    scroll-snap-align: start;
  }

  .btn {
    width: 100%;
  }
}
</style>
